/**
 * Get Ant Design theme tokens
 */
export const getDesignTokens = (mode, theme) => {
  return {
    // Emit antd tokens as CSS custom properties under the `pos-` prefix,
    // exactly as POS-frontend does (Helpers/ats.helper.js getDesignTokens).
    // The pos-mirror sale-bill screen uses POS's own stylesheet verbatim,
    // which references var(--pos-padding), var(--pos-color-border) etc.
    // With this in place those resolve from DEPO's tokens, so the screen
    // matches POS pixel-for-pixel and still follows depo's light/dark mode
    // instead of needing the values hardcoded.
    cssVar: { prefix: "pos", key: "pos-theme" },
    algorithm: mode ? theme.defaultAlgorithm : theme.darkAlgorithm,
    token: {
      colorPrimary: '#4361ee',
      borderRadius: 8,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: 14,
      colorBgLayout: '#f5f7fa',
      controlHeight: 36,
      colorLink: '#4361ee',
      colorBorder: '#e5e7eb',
      colorBorderSecondary: '#f3f4f6',
      colorTextSecondary: '#6b7280',
      colorTextTertiary: '#9ca3af',
    },
    components: {
      Card: {
        headerBg: 'transparent',
        paddingLG: 20,
      },
      Table: {
        headerBg: '#f9fafb',
        headerColor: '#374151',
        headerSplitColor: '#e5e7eb',
        rowHoverBg: '#f0f4ff',
        borderColor: '#f3f4f6',
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
        cellFontSize: 13,
        headerFontSize: 12,
        headerFontWeight: 600,
      },
      Button: {
        controlHeight: 36,
        primaryShadow: 'none',
        fontWeight: 500,
      },
      Form: {
        itemMarginBottom: 16,
        verticalLabelPadding: '0 0 4px',
      },
      Menu: {
        itemBg: '#ffffff',
        subMenuItemBg: '#fafbfc',
        itemSelectedBg: '#eef2ff',
        itemSelectedColor: '#4361ee',
        itemHoverBg: '#f5f7fa',
        itemColor: '#4b5563',
        itemBorderRadius: 6,
        itemMarginInline: 8,
        itemMarginBlock: 2,
        iconSize: 16,
        fontSize: 13,
      },
      Descriptions: {
        titleMarginBottom: 12,
      },
      Input: {
        controlHeight: 38,
      },
      Select: {
        controlHeight: 38,
      },
      DatePicker: {
        controlHeight: 38,
      },
      InputNumber: {
        controlHeight: 38,
      },
      Tag: {
        borderRadiusSM: 4,
      },
      Pagination: {
        itemSize: 32,
        fontSize: 13,
      },
    },
  };
};

/**
 * Format date
 */
export const formatDate = (date) => {
  if (!date) return "-";
  try {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

/**
 * Format date time
 */
export const formatDateTime = (date) => {
  if (!date) return "-";
  try {
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "-";
  }
};

/**
 * Format currency (INR)
 */
export const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return "-";
  return `\u20B9${Number(value).toLocaleString("en-IN")}`;
};

/**
 * Format number
 */
export const formatNumber = (value) => {
  if (value == null || isNaN(value)) return "-";
  return Number(value).toLocaleString("en-IN");
};

// ───────────────────────────────────────────────────────────────────
// FSSAI gate — keep in sync with backend `assertFssaiValid` in
// invoiceController.js. WW / WWQ / WWP / PUC stores need a valid
// FSSAI license to be invoiced. Paint substores of those types are
// Combo stores (WW+Paint, WWQ+Paint, WWP+Paint, PUC+Paint) still sell
// food and need a valid FSSAI licence — only PURE paint outlets (paint
// flag with no food store_type) are exempt. Driven by store_type alone.
// ───────────────────────────────────────────────────────────────────
const FSSAI_REQUIRED_STORE_TYPES = new Set([
  "wonder_world",
  "wonder_world_quick",
  "wwp",
  "puc",
]);

export const isFssaiRequiredForStore = ({ store_type } = {}) => {
  return FSSAI_REQUIRED_STORE_TYPES.has(store_type);
};

/**
 * Returns { required, valid, reason } for the FSSAI gate.
 *   required === false  → invoice button stays enabled
 *   required === true   → check `valid`. If false, `reason` is a
 *                         user-facing string for the blocking message.
 */
export const getFssaiStatus = ({
  store_type,
  is_paint_store,
  fssai_no,
  fssai_valid_till,
} = {}) => {
  const required = isFssaiRequiredForStore({ store_type, is_paint_store });
  if (!required) return { required: false, valid: true, reason: "" };

  const trimmedNo = (fssai_no || "").toString().trim();
  if (!trimmedNo) {
    return { required: true, valid: false, reason: "FSSAI license is missing on this store." };
  }
  if (fssai_valid_till) {
    const expiry = new Date(fssai_valid_till);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (expiry < today) {
      return {
        required: true,
        valid: false,
        reason: `FSSAI license expired on ${expiry.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}.`,
      };
    }
  }
  return { required: true, valid: true, reason: "" };
};

// ───────────────────────────────────────────────────────────────────
// Min-invoice-value gate — pre-flight check that mirrors backend
// `assertMinInvoiceValue` in invoiceController.js. Source of truth
// lives in Configurations.config_key='min_invoice_values' (jsonb
// object keyed by store_type). Falsy / missing entry / value ≤ 0
// means "no floor for this store type".
// ───────────────────────────────────────────────────────────────────

const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN")}`;

// ───────────────────────────────────────────────────────────────────
// Brand-restriction gate — pre-flight check that mirrors backend
// `assertItemsBrandAllowed` in invoiceController.js. The frontend
// fetches the resolved filter via /invoices/brand-filter and uses
// these helpers to mark restricted items + block save.
// ───────────────────────────────────────────────────────────────────

/**
 * @param {{restricted, brand_ids, exclude_brand_ids}} filter — payload from
 *        apiService.getBrandFilter. `null` / `{restricted: false}` → no
 *        rule applies, every item is allowed.
 * @param {number|string} brandId — item's brand_id (may be null/undef
 *        if MDM doesn't have one; treat as allowed).
 */
export const isBrandBlocked = (filter, brandId) => {
  if (!filter || !filter.restricted) return false;
  if (brandId == null) return false;
  const includes = filter.brand_ids || [];
  const excludes = filter.exclude_brand_ids || [];
  if (includes.length > 0 && !includes.includes(brandId)) return true;
  if (excludes.length > 0 && excludes.includes(brandId)) return true;
  return false;
};

/**
 * Filter an item list to those blocked by brand restrictions.
 * Returns [] when no rule applies or every item is allowed.
 */
export const getBrandBlockedItems = (filter, items = []) => {
  if (!filter || !filter.restricted) return [];
  return items.filter((it) => isBrandBlocked(filter, it.brand_id));
};

// ───────────────────────────────────────────────────────────────────
// Store-balance gate — pre-flight check that mirrors backend
// `assertInvoiceWithinBalance` in invoiceController.js. Balance comes
// from SAP /zservice_fi_puc; the backend exposes it via
// /invoices/store-balance for display + UI gating.
// ───────────────────────────────────────────────────────────────────

const formatRupees = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * @param {object} balanceResp — `{success, amount, remark, available_balance}`
 *        as returned by apiService.getStoreBalance. `null`/missing
 *        means "balance unknown" → check silently allows (matches
 *        backend behaviour on SAP-API failure).
 * @param {number} totalAmount — current invoice total in rupees
 * @returns {{ available, valid, reason, label }}
 *   valid=false → block; reason is user-facing
 *   valid=true  → allow
 */
export const getStoreBalanceStatus = (balanceResp, totalAmount) => {
  if (!balanceResp || balanceResp.success === false) {
    return { available: null, valid: true, reason: "", label: "Balance unavailable" };
  }
  const available = Number(balanceResp.available_balance ?? 0);
  const remark = balanceResp.remark || "";
  const total = Number(totalAmount || 0);
  // Label for display: credit shows positive, debit shows the amount owed.
  const label = available >= 0
    ? `${formatRupees(available)} credit${remark ? ` (${remark})` : ""}`
    : `${formatRupees(Math.abs(available))} debit${remark ? ` (${remark})` : ""}`;
  if (total > available) {
    return {
      available,
      valid: false,
      reason: `Invoice total ${formatRupees(total)} exceeds store's available balance ${formatRupees(available)}${remark ? ` (${remark})` : ""}. Settle in SAP before confirming.`,
      label,
    };
  }
  return { available, valid: true, reason: "", label };
};

/**
 * @param {{store_type, is_paint_store}} store — invoice's store snapshot.
 *        store_type wins over paint flag, so combo stores (paint +
 *        WW/WWQ/WWP/PUC) follow the food-side minimum. Only stores
 *        with no food store_type fall under `_paint`.
 * @param {number} totalAmount — current invoice total in rupees
 * @param {object} minValues — parsed config_value from admin's
 *                             "Minimum Invoice Values" page. Pass {}
 *                             when config row is missing.
 * @returns {{ required, valid, minAmount, reason }}
 *   required=false → no floor applies; valid stays true
 *   required=true + valid=false → block; `reason` is user-facing
 */
export const getMinInvoiceStatus = (store, totalAmount, minValues = {}) => {
  // Back-compat: callers used to pass a bare store_type string.
  // Tolerate both shapes so we don't have to update every call site.
  const storeObj = typeof store === "string" ? { store_type: store } : (store || {});
  const { store_type, is_paint_store } = storeObj;

  if (!minValues || (!store_type && !is_paint_store)) {
    return { required: false, valid: true, minAmount: 0, reason: "" };
  }

  let minAmount = 0;
  let label = "";
  if (store_type && minValues[store_type] != null) {
    minAmount = parseFloat(minValues[store_type] || 0);
    label = store_type.replace(/_/g, " ");
  } else if (is_paint_store === true && minValues._paint != null) {
    minAmount = parseFloat(minValues._paint || 0);
    label = "paint store";
  }

  if (!(minAmount > 0)) {
    return { required: false, valid: true, minAmount: 0, reason: "" };
  }
  const total = Number(totalAmount || 0);
  if (total < minAmount) {
    return {
      required: true,
      valid: false,
      minAmount,
      reason: `Invoice total ${formatINR(total)} is below the minimum ${formatINR(minAmount)} required for ${label}.`,
    };
  }
  return { required: true, valid: true, minAmount, reason: "" };
};
